import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Trash, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

/**
 * Dialog reutilizable para pedir un motivo antes de eliminar cualquier
 * captura. El motivo se pasa como header `X-Delete-Reason` al backend
 * (via `api.delete(url, { headers: { 'X-Delete-Reason': reason } })`).
 *
 * Uso vía hook:
 *   const confirmDelete = useConfirmDelete();
 *   const ok = await confirmDelete({
 *     title: "¿Eliminar bitácora?",
 *     description: "Se registrará en el historial de auditoría.",
 *     itemLabel: `Bitácora del ${log.date}`,
 *     minLength: 8,
 *   });
 *   if (!ok) return;
 *   await api.delete(`/bitacora/logs/${id}`, { headers: { "X-Delete-Reason": ok.reason }});
 */

let _resolvePromise = null;
let _setOpen = null;
let _setProps = null;

export function useConfirmDelete() {
  return useCallback((opts = {}) => {
    if (!_setOpen || !_setProps) {
      // Fallback nativo si el mount raíz aún no está listo
      const reason = window.prompt(opts.title || "Motivo de la eliminación:");
      return Promise.resolve(reason ? { reason } : null);
    }
    return new Promise((resolve) => {
      _resolvePromise = resolve;
      _setProps({
        title: opts.title || "¿Eliminar registro?",
        description: opts.description
          || "Esta acción quedará registrada en el historial de auditoría minera con tu nombre, la fecha y el motivo. No se puede deshacer.",
        itemLabel: opts.itemLabel || "",
        minLength: opts.minLength ?? 8,
        confirmLabel: opts.confirmLabel || "Eliminar",
        destructive: opts.destructive !== false,
      });
      _setOpen(true);
    });
  }, []);
}

/**
 * Montaje raíz — se coloca UNA VEZ en App.js. Expone el dialogo global.
 */
export default function ConfirmDeleteRoot() {
  const [open, setOpen] = useState(false);
  const [props, setProps] = useState({});
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    _setOpen = setOpen;
    _setProps = setProps;
    return () => { _setOpen = null; _setProps = null; };
  }, []);

  useEffect(() => {
    if (open) {
      setReason("");
      setSubmitting(false);
      // autofocus tras un tick para dejar que el dialog termine su animación
      const t = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [open]);

  const cancel = useCallback(() => {
    setOpen(false);
    _resolvePromise?.(null);
    _resolvePromise = null;
  }, []);

  const confirm = useCallback(() => {
    const clean = (reason || "").trim();
    if (clean.length < (props.minLength ?? 8)) return;
    setSubmitting(true);
    setOpen(false);
    _resolvePromise?.({ reason: clean });
    _resolvePromise = null;
  }, [reason, props.minLength]);

  const minLen = props.minLength ?? 8;
  const clean = (reason || "").trim();
  const canConfirm = clean.length >= minLen && !submitting;

  return createPortal(
    <Dialog open={open} onOpenChange={(o) => { if (!o) cancel(); }}>
      <DialogContent
        className="max-w-md"
        aria-describedby="confirm-delete-desc"
        data-testid="confirm-delete-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Warning size={20} weight="fill" /> {props.title}
          </DialogTitle>
          <DialogDescription id="confirm-delete-desc">
            {props.description}
          </DialogDescription>
        </DialogHeader>
        {props.itemLabel && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-sm text-slate-800"
                data-testid="confirm-delete-item">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 block">Registro</span>
            <span className="font-medium">{props.itemLabel}</span>
          </div>
        )}
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center justify-between">
            <span>Motivo <span className="text-red-600">*</span></span>
            <span className={`font-mono ${clean.length < minLen ? "text-red-500" : "text-emerald-600"}`}>
              {clean.length}/{minLen} mín.
            </span>
          </label>
          <Textarea
            ref={inputRef}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 400))}
            placeholder="Ej: error de captura, colaborador duplicado, dato incorrecto…"
            className="mt-1 min-h-[80px]"
            data-testid="confirm-delete-reason-input"
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canConfirm) confirm();
            }}
          />
          <p className="text-[10px] text-slate-500 mt-1">
            Cmd/Ctrl+Enter para confirmar. Máximo 400 caracteres.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={cancel}
            data-testid="confirm-delete-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={confirm}
            disabled={!canConfirm}
            className={props.destructive
              ? "bg-red-600 hover:bg-red-700 text-white gap-1.5"
              : "bg-slate-900 hover:bg-slate-800 text-white gap-1.5"}
            data-testid="confirm-delete-confirm"
          >
            <Trash size={14} weight="bold" /> {props.confirmLabel || "Eliminar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>,
    document.body,
  );
}
